import APP_CONFIG from '../config';
import { FEATURE_FLAGS, isFeatureEnabled } from '@/constants/feature-flags';

export enum FEATURES {
  CHECKBOX = 'checkbox',
  TEXT_BOX = 'text_box',
  TEXT_AREA = 'text_area',
  DROPDOWN = 'dropdown',
  PAYMENT = 'payment',
  SIGNING_ORDER = 'signing_order',
  LINE_ITEMS = 'line_items',
  MULTIPLE_TEMPLATES = 'multiple_templates',
  AUTO_SEND = 'auto_send',
  FILLABLE_FORMS = 'fillable_forms',
  OTP = 'otp',
  FORM_LOGO = 'form_logo',
}

// Plan keys as defined in APP_CONFIG.pricingPlans
export type PlanKey =
  | 'free'
  | 'starter'
  | 'basic'
  | 'standard'
  | 'pro'
  | 'enterprise'
  | 'gs_enterprise_m'
  | 'gs_enterprise_l'
  | 'gs_enterprise_xl'
  | 'gs_enterprise_xxl';

const FEATURE_ALLOWED_PLANS: Record<FEATURES, PlanKey[]> = {
  [FEATURES.CHECKBOX]: [
    'standard',
    'pro',
    'enterprise',
    'gs_enterprise_m',
    'gs_enterprise_l',
    'gs_enterprise_xl',
    'gs_enterprise_xxl',
  ],
  [FEATURES.TEXT_BOX]: [
    'standard',
    'pro',
    'enterprise',
    'gs_enterprise_m',
    'gs_enterprise_l',
    'gs_enterprise_xl',
    'gs_enterprise_xxl',
  ],
  [FEATURES.TEXT_AREA]: [
    'starter',
    'basic',
    'standard',
    'pro',
    'enterprise',
    'gs_enterprise_m',
    'gs_enterprise_l',
    'gs_enterprise_xl',
    'gs_enterprise_xxl',
  ],
  [FEATURES.DROPDOWN]: [
    'standard',
    'pro',
    'enterprise',
    'gs_enterprise_m',
    'gs_enterprise_l',
    'gs_enterprise_xl',
    'gs_enterprise_xxl',
  ],
  [FEATURES.PAYMENT]: [
    'enterprise',
    'gs_enterprise_m',
    'gs_enterprise_l',
    'gs_enterprise_xl',
    'gs_enterprise_xxl',
  ],
  [FEATURES.SIGNING_ORDER]: [
    'standard',
    'pro',
    'enterprise',
    'gs_enterprise_m',
    'gs_enterprise_l',
    'gs_enterprise_xl',
    'gs_enterprise_xxl',
  ],
  [FEATURES.LINE_ITEMS]: [
    'standard',
    'pro',
    'enterprise',
    'gs_enterprise_m',
    'gs_enterprise_l',
    'gs_enterprise_xl',
    'gs_enterprise_xxl',
  ],
  [FEATURES.MULTIPLE_TEMPLATES]: [
    'pro',
    'enterprise',
    'gs_enterprise_m',
    'gs_enterprise_l',
    'gs_enterprise_xl',
    'gs_enterprise_xxl',
  ],
  [FEATURES.AUTO_SEND]: [
    'standard',
    'pro',
    'enterprise',
    'gs_enterprise_m',
    'gs_enterprise_l',
    'gs_enterprise_xl',
    'gs_enterprise_xxl',
  ],
  [FEATURES.FILLABLE_FORMS]: [
    'pro',
    'enterprise',
    'gs_enterprise_m',
    'gs_enterprise_l',
    'gs_enterprise_xl',
    'gs_enterprise_xxl',
  ],
  [FEATURES.OTP]: [
    'pro',
    'enterprise',
    'gs_enterprise_m',
    'gs_enterprise_l',
    'gs_enterprise_xl',
    'gs_enterprise_xxl',
  ],
  [FEATURES.FORM_LOGO]: [
    'standard',
    'pro',
    'enterprise',
    'gs_enterprise_m',
    'gs_enterprise_l',
    'gs_enterprise_xl',
    'gs_enterprise_xxl',
  ],
};
// Map of feature to the allowed plans
const FEATURE_NEW_ALLOWED_PLANS: Record<FEATURES, PlanKey[]> = {
  [FEATURES.CHECKBOX]: [
    'starter',
    'basic',
    'standard',
    'pro',
    'enterprise',
    'gs_enterprise_m',
    'gs_enterprise_l',
    'gs_enterprise_xl',
    'gs_enterprise_xxl',
  ],
  [FEATURES.TEXT_BOX]: [
    'starter',
    'basic',
    'standard',
    'pro',
    'enterprise',
    'gs_enterprise_m',
    'gs_enterprise_l',
    'gs_enterprise_xl',
    'gs_enterprise_xxl',
  ],
  [FEATURES.TEXT_AREA]: [
    'starter',
    'basic',
    'standard',
    'pro',
    'enterprise',
    'gs_enterprise_m',
    'gs_enterprise_l',
    'gs_enterprise_xl',
    'gs_enterprise_xxl',
  ],
  [FEATURES.DROPDOWN]: [
    'starter',
    'basic',
    'standard',
    'pro',
    'enterprise',
    'gs_enterprise_m',
    'gs_enterprise_l',
    'gs_enterprise_xl',
    'gs_enterprise_xxl',
  ],
  [FEATURES.PAYMENT]: [
    'pro',
    'enterprise',
    'gs_enterprise_m',
    'gs_enterprise_l',
    'gs_enterprise_xl',
    'gs_enterprise_xxl',
  ],
  [FEATURES.SIGNING_ORDER]: [
    'basic',
    'standard',
    'pro',
    'enterprise',
    'gs_enterprise_m',
    'gs_enterprise_l',
    'gs_enterprise_xl',
    'gs_enterprise_xxl',
  ],
  [FEATURES.LINE_ITEMS]: [
    'starter',
    'basic',
    'standard',
    'pro',
    'enterprise',
    'gs_enterprise_m',
    'gs_enterprise_l',
    'gs_enterprise_xl',
    'gs_enterprise_xxl',
  ],
  [FEATURES.MULTIPLE_TEMPLATES]: [
    'pro',
    'enterprise',
    'gs_enterprise_m',
    'gs_enterprise_l',
    'gs_enterprise_xl',
    'gs_enterprise_xxl',
  ],
  [FEATURES.AUTO_SEND]: [
    'standard',
    'pro',
    'enterprise',
    'gs_enterprise_m',
    'gs_enterprise_l',
    'gs_enterprise_xl',
    'gs_enterprise_xxl',
  ],
  [FEATURES.FILLABLE_FORMS]: [
    'starter',
    'basic',
    'standard',
    'pro',
    'enterprise',
    'gs_enterprise_m',
    'gs_enterprise_l',
    'gs_enterprise_xl',
    'gs_enterprise_xxl',
  ],
  [FEATURES.OTP]: [
    'pro',
    'enterprise',
    'gs_enterprise_m',
    'gs_enterprise_l',
    'gs_enterprise_xl',
    'gs_enterprise_xxl',
  ],
  [FEATURES.FORM_LOGO]: [
    'standard',
    'pro',
    'enterprise',
    'gs_enterprise_m',
    'gs_enterprise_l',
    'gs_enterprise_xl',
    'gs_enterprise_xxl',
  ],
};

const SEAT_BASED_PLANS = [
  '3seats',
  'gs15seats',
  '30seats',
  '60seats',
  '120seats',
  '240seats',
  '400seats',
  '600seats',
  '1000seats',
  '1000plus',
];

// Build a reverse lookup: planId (value) -> planKey (key)
const PLAN_ID_TO_KEY: Record<string, PlanKey> = Object.entries(
  APP_CONFIG.pricingPlans,
).reduce((acc, [key, value]) => {
  if (typeof value === 'string') {
    acc[value] = key as PlanKey;
  }
  return acc;
}, {} as Record<string, PlanKey>);

/**
 * Normalizes a full plan ID to its simplified plan key using APP_CONFIG.pricingPlans.
 * E.g., 'gs_free' -> 'free', 'gs_standard_85' -> 'standard', 'pro' -> 'pro'
 * @param planId - The full plan ID string
 * @returns The simplified plan key
 */
function normalizePlanKey(planId: string): PlanKey {
  return PLAN_ID_TO_KEY[planId] || (planId as PlanKey);
}

/**
 * Checks if a feature is available for a given plan.
 * @param plan - The current plan key (e.g., 'free', 'starter', ...)
 * @param feature - The feature enum value from FEATURES
 * @returns boolean
 */
export function isFeatureAvailable(plan: PlanKey, feature: FEATURES): boolean {
  if (SEAT_BASED_PLANS.includes(plan)) {
    return true;
  }
  const normalizedPlan = normalizePlanKey(plan);
  const allowedPlans = isFeatureEnabled(FEATURE_FLAGS.NEW_PRICING)
    ? FEATURE_NEW_ALLOWED_PLANS[feature]
    : FEATURE_ALLOWED_PLANS[feature];
  if (!allowedPlans) return true; // If not gated, available to all
  return allowedPlans.includes(normalizedPlan);
}
